import { z } from 'zod';
import { selectionPoolItemSchema, selectionPoolKpiSchema } from '../analytics/analytics.schema';

/**
 * Persistenter Selektionspool (ADR 0047): benannte, team-geteilte, bearbeitbare
 * Auswahllisten je Entitätsart. Speichert nur Referenzen — die Anzeige-Items +
 * Kennzahlen kommen aus der Analytics-Auflösung (Wiederverwendung ADR 0046).
 */

/** Art eines Pools — bestimmt, welche Member-Kinds zulässig sind. */
export const selectionPoolEntityKindSchema = z.enum(['CUSTOMER', 'DOCUMENT', 'ARTICLE']);
export type SelectionPoolEntityKind = z.infer<typeof selectionPoolEntityKindSchema>;

/** Konkreter Zieltyp eines Mitglieds (DOCUMENT → Verkauf ODER Einkauf). */
export const selectionPoolMemberKindSchema = z.enum([
  'CUSTOMER',
  'SALES_DOCUMENT',
  'PURCHASE_DOCUMENT',
  'ARTICLE',
]);
export type SelectionPoolMemberKind = z.infer<typeof selectionPoolMemberKindSchema>;

/** Welche Member-Kinds ein Pool der jeweiligen Art aufnehmen darf (Service-Validierung). */
export const MEMBER_KINDS_BY_ENTITY: Record<SelectionPoolEntityKind, SelectionPoolMemberKind[]> = {
  CUSTOMER: ['CUSTOMER'],
  ARTICLE: ['ARTICLE'],
  DOCUMENT: ['SALES_DOCUMENT', 'PURCHASE_DOCUMENT'],
};

/**
 * Brücke zwischen dem `kind` der Analytics-Items (kleingeschrieben, ADR 0046)
 * und dem `SelectionPoolMemberKind` (großgeschrieben, Prisma). Eine Quelle der
 * Wahrheit für Front- und Backend, damit „Item → Mitglied" konsistent bleibt.
 */
export const MEMBER_KIND_BY_ITEM_KIND = {
  customer: 'CUSTOMER',
  article: 'ARTICLE',
  sales_document: 'SALES_DOCUMENT',
  purchase_document: 'PURCHASE_DOCUMENT',
} as const satisfies Record<string, SelectionPoolMemberKind>;

export const ITEM_KIND_BY_MEMBER_KIND = {
  CUSTOMER: 'customer',
  ARTICLE: 'article',
  SALES_DOCUMENT: 'sales_document',
  PURCHASE_DOCUMENT: 'purchase_document',
} as const;

export const createSelectionPoolSchema = z.object({
  name: z.string().trim().min(1).max(120),
  entity: selectionPoolEntityKindSchema,
  description: z.string().trim().max(2000).optional(),
});
export type CreateSelectionPoolInput = z.infer<typeof createSelectionPoolSchema>;

/** Nur Name/Beschreibung änderbar — Art (`entity`) ist nach Anlage fix. */
export const updateSelectionPoolSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(2000).nullish(),
});
export type UpdateSelectionPoolInput = z.infer<typeof updateSelectionPoolSchema>;

/** Referenz auf einen Datensatz beim Hinzufügen/Entfernen. */
export const selectionPoolMemberRefSchema = z.object({
  kind: selectionPoolMemberKindSchema,
  id: z.string().uuid(),
});
export type SelectionPoolMemberRef = z.infer<typeof selectionPoolMemberRefSchema>;

export const mutateSelectionPoolMembersSchema = z.object({
  members: z.array(selectionPoolMemberRefSchema).min(1).max(1000),
});
export type MutateSelectionPoolMembersInput = z.infer<typeof mutateSelectionPoolMembersSchema>;

/** Listen-/Kopfzeile eines Pools. `canManage` = darf umbenennen/löschen (Ersteller/Admin). */
export const selectionPoolSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  entity: selectionPoolEntityKindSchema,
  description: z.string().nullable(),
  memberCount: z.number().int(),
  createdBy: z.string().uuid(),
  createdByName: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  canManage: z.boolean(),
});
export type SelectionPoolSummary = z.infer<typeof selectionPoolSummarySchema>;

/** Detail: Kopf + aufgelöste Live-Items (aus der Analytics-Auflösung) + Kennzahlen. */
export const selectionPoolDetailSchema = z.object({
  pool: selectionPoolSummarySchema,
  items: z.array(selectionPoolItemSchema),
  kpis: z.array(selectionPoolKpiSchema),
});
export type SelectionPoolDetail = z.infer<typeof selectionPoolDetailSchema>;

/** Antwort beim Hinzufügen/Entfernen: wie viele Referenzen tatsächlich wirkten. */
export const mutateSelectionPoolMembersResultSchema = z.object({
  added: z.number().int().optional(),
  removed: z.number().int().optional(),
  memberCount: z.number().int(),
});
export type MutateSelectionPoolMembersResult = z.infer<typeof mutateSelectionPoolMembersResultSchema>;
